/**
 * NotesSection — unified "Estado no Zoho CRM & Notas" block.
 *
 * Layout (20/02/2026 restructure):
 *   • Zoho status picker (top action).
 *   • Sub-block "Notas do Atendimento"
 *       - manual textarea + save → note synced to Zoho, kind=manual,
 *         author_role stamped server-side.
 *       - AI button "Nota IA → Estimativa" opens ``AINoteModal`` that
 *         previews an editable draft; on confirm the note is saved
 *         AND injected into ``estimate.additional_notes`` so it shows
 *         on the client-facing document.
 *   • Sub-block "Notas do Consultor"
 *       - same shape but the AI target is ``proposal.additional_notes``.
 *
 * Filtering rule (option c=iii): notes without ``author_role`` (legacy)
 * are hidden.  All notes still land in the same ``lead.notes`` array on
 * the backend — the split is purely display-side.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Sparkles, FileText, CheckCircle2, AlertTriangle,
  Headphones, Stethoscope, Wand2, Save, X,
} from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";


// ---------------------------------------------------------------------------
// Modal that shows an AI-generated draft and lets the user edit before
// applying it to the estimate/proposal.
// ---------------------------------------------------------------------------
function AINoteModal({ open, onOpenChange, target, leadId, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState("");

  const targetLabel = target === "estimate" ? "Estimativa" : "Proposta";
  const targetHint = target === "estimate"
    ? "O texto vai substituir as 'Notas adicionais' da estimativa que o cliente vê."
    : "O texto vai substituir as 'Notas adicionais' da proposta que o cliente vê.";

  const preview = async () => {
    setLoading(true);
    setContent("");
    try {
      const r = await api.post(`/leads/${leadId}/notes/ai-preview`, { target });
      setContent(r.data?.content || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar nota IA");
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate the first draft when the modal opens. shadcn's Dialog
  // fires ``onOpenChange`` only on close, so we drive the preview off
  // the ``open`` prop directly.
  useEffect(() => {
    if (open && !content && !loading) preview();
    if (!open) setContent("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const apply = async () => {
    const text = (content || "").trim();
    if (!text) { toast.error("Conteúdo vazio"); return; }
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/notes/ai-apply`, { target, content: text });
      toast.success(`Nota IA aplicada à ${targetLabel} e enviada ao Zoho`);
      onApplied?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao aplicar nota");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`ai-note-modal-${target}`} className="max-w-2xl bg-card">
        <DialogHeader>
          <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Nota IA para a {targetLabel}
          </DialogTitle>
          <DialogDescription>{targetHint}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">
              Rascunho (editável)
            </label>
            <button
              type="button"
              onClick={preview}
              disabled={loading}
              data-testid={`ai-note-regen-${target}`}
              className="inline-flex items-center gap-1.5 text-[11px] bg-muted hover:bg-muted border border-border rounded-full px-3 py-1 font-semibold disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
              {loading ? "A gerar…" : "Regenerar com IA"}
            </button>
          </div>
          <textarea
            data-testid={`ai-note-textarea-${target}`}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={loading || saving}
            rows={10}
            placeholder={loading ? "A pedir texto à IA…" : "O rascunho vai aparecer aqui — podes editá-lo antes de aplicar."}
            className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary resize-y min-h-[220px] disabled:opacity-70"
          />
          <div className="text-[11px] text-muted-foreground leading-relaxed">
            Ao aplicar, o texto substitui as <b>Notas adicionais</b> na {targetLabel.toLowerCase()} e é
            também gravado nas notas do lead (com sincronização para o Zoho CRM).
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid={`ai-note-cancel-${target}`}
            className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 font-semibold disabled:opacity-50"
          >
            <X className="w-3.5 h-3.5" /> Cancelar
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={saving || loading || !content.trim()}
            data-testid={`ai-note-apply-${target}`}
            className="inline-flex items-center gap-1.5 text-xs bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-4 py-2 font-semibold disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? "A aplicar…" : `Aplicar à ${targetLabel}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Sub-block used twice inside NotesSection — once for Atendimento and
// once for Consultor. Only the labels, filter role, and AI target
// change between the two.
// ---------------------------------------------------------------------------
function NotesSubBlock({
  lead, onChanged, roleFilter, title, subtitle,
  icon: Icon, colorClass, aiTarget, aiButtonLabel, testidPrefix,
}) {
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);

  // Client-side filter: only notes whose author_role matches one of the
  // roles passed in `roleFilter`. Legacy notes without author_role are
  // hidden per user's choice (option c=iii).
  const notes = (lead?.notes || []).filter((n) => {
    const r = (n.author_role || "").toLowerCase();
    return r && roleFilter.includes(r);
  }).slice().reverse();

  const save = async () => {
    const content = (newNote || "").trim();
    if (!content) { toast.error("Nota vazia"); return; }
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/notes`, { content });
      toast.success("Nota gravada e enviada ao Zoho");
      setNewNote("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar nota");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`${testidPrefix}-block`} className="border border-border rounded-xl p-4 lg:p-5 bg-muted/40">
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-full ${colorClass.bg} border ${colorClass.border} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${colorClass.icon}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.15em] font-semibold ${colorClass.label}`}>
            {subtitle}
          </div>
          <div className="text-sm font-ui text-foreground">{title}</div>
        </div>
      </div>

      <textarea
        data-testid={`${testidPrefix}-input`}
        value={newNote}
        onChange={(e) => setNewNote(e.target.value)}
        rows={3}
        placeholder="Escreve uma nota interna (será sincronizada com o Zoho)…"
        className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary resize-y min-h-[70px]"
      />

      <div className="flex flex-wrap items-center gap-2 justify-end mt-2.5">
        <button
          type="button"
          onClick={() => setAiModalOpen(true)}
          data-testid={`${testidPrefix}-ai-btn`}
          className="inline-flex items-center gap-1.5 bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5 text-xs font-semibold text-foreground"
          title="Abrir janela editável com texto IA para o documento do cliente"
        >
          <Sparkles className="w-3 h-3" /> {aiButtonLabel}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !newNote.trim()}
          data-testid={`${testidPrefix}-save-btn`}
          className="inline-flex items-center gap-1.5 bg-foreground hover:bg-foreground/90 text-background rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
          Guardar nota
        </button>
      </div>

      {notes.length > 0 && (
        <div className="mt-4 space-y-2" data-testid={`${testidPrefix}-list`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
            Notas anteriores <span className="text-muted-foreground">({notes.length})</span>
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
            {notes.map((n) => (
              <div key={n.id} data-testid={`${testidPrefix}-item-${n.id}`}
                   className="rounded-lg border border-border bg-card p-3 text-xs">
                <div className="flex items-center gap-2 mb-1 text-[10px] text-muted-foreground flex-wrap">
                  <span className="font-semibold text-foreground">{n.author_name || "—"}</span>
                  {n.ai_generated && (
                    <span className="inline-flex items-center gap-1 bg-[hsl(var(--info-bg))] border border-[hsl(var(--info))]/25 text-[hsl(var(--info))] rounded-full px-1.5 py-0.5 font-semibold">
                      <Sparkles className="w-2.5 h-2.5" /> IA
                    </span>
                  )}
                  {n.kind === "ai_estimate" && (
                    <span className="inline-flex items-center gap-1 bg-[hsl(var(--info-bg))] border border-[hsl(var(--info))]/25 text-[hsl(var(--info))] rounded-full px-1.5 py-0.5 font-semibold">
                      → Estimativa
                    </span>
                  )}
                  {n.kind === "ai_proposal" && (
                    <span className="inline-flex items-center gap-1 bg-[hsl(var(--info-bg))] border border-[hsl(var(--info))]/25 text-[hsl(var(--info))] rounded-full px-1.5 py-0.5 font-semibold">
                      → Proposta
                    </span>
                  )}
                  {n.kind === "disqualification_reason" && (
                    <span className="inline-flex items-center gap-1 bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 text-[hsl(var(--danger))] rounded-full px-1.5 py-0.5 font-semibold">
                      <AlertTriangle className="w-2.5 h-2.5" /> Desqualificação
                    </span>
                  )}
                  <span className="ml-auto">{n.created_at ? new Date(n.created_at).toLocaleString("pt-PT") : ""}</span>
                  {n.zoho_note_id ? (
                    <span className="inline-flex items-center gap-1 bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 text-[hsl(var(--success))] rounded-full px-1.5 py-0.5 text-[9px] font-semibold" title={`Zoho note id ${n.zoho_note_id}`}>
                      <CheckCircle2 className="w-2.5 h-2.5" /> Zoho
                    </span>
                  ) : n.zoho_sync_error ? (
                    <span className="inline-flex items-center gap-1 bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 text-[hsl(var(--warning))] rounded-full px-1.5 py-0.5 text-[9px] font-semibold" title={n.zoho_sync_error}>
                      <AlertTriangle className="w-2.5 h-2.5" /> Zoho falhou
                    </span>
                  ) : null}
                </div>
                <div className="text-foreground whitespace-pre-wrap">{n.content}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AINoteModal
        open={aiModalOpen}
        onOpenChange={setAiModalOpen}
        target={aiTarget}
        leadId={lead.id}
        onApplied={onChanged}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Top-level NotesSection — used inside LeadDetail.jsx
//
// 20/02/2026 — Consultor sub-block was removed here. The Consultor's
// notes are now captured exclusively by ``RTNotesBlock`` (AI-powered
// split into client-facing and internal text; the client-facing side
// is applied to the proposal, the internal side flows to Zoho). This
// component keeps only the Atendimento sub-block to avoid duplicate
// consultor entries on the same page.
// ---------------------------------------------------------------------------
export default function NotesSection({ lead, onChanged }) {
  return (
    <div className="space-y-4" data-testid="notes-subblocks">
      <NotesSubBlock
        lead={lead}
        onChanged={onChanged}
        roleFilter={["atendimento"]}
        title="Notas do Atendimento"
        subtitle="Fase 1 — SAC"
        icon={Headphones}
        colorClass={{
          bg: "bg-[hsl(var(--info-bg))]",
          border: "border-[#BFDBFE]",
          icon: "text-primary",
          label: "text-primary",
        }}
        aiTarget="estimate"
        aiButtonLabel="Nota IA → Estimativa"
        testidPrefix="notes-atendimento"
      />
    </div>
  );
}
